import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Save, Check, Building2, Mail, UserPlus, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SUPPORT_AREAS } from '../constants/supportAreas';

const GRADES = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
const TIERS = ['Tier 1', 'Tier 2', 'Tier 3'];
const STATUSES = ['New Student', 'Improving', 'Limited Progress', 'Needs Review'];

export default function AddStudentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedSchoolId, isSuperAdmin, user, userSchoolId, userSchoolName } = useAuth();
  const [loading, setLoading] = useState(false);
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [staffContacts, setStaffContacts] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [teacherEntryMode, setTeacherEntryMode] = useState('select'); // 'select' or 'email'
  const [teacherEmail, setTeacherEmail] = useState('');
  const [teacherDisplayName, setTeacherDisplayName] = useState('');
  const [creatingTeacher, setCreatingTeacher] = useState(false);
  
  // For non-Super Admins (School Admins, Intervention Providers), check if they have an assigned school
  const hasSchoolAssignment = isSuperAdmin || !!userSchoolId;
  
  // Get school_id from URL params, or from auth context
  // For Super Admin: uses selectedSchoolId (from dropdown) or URL param
  // For School Admin / Intervention Provider: uses user.school_id
  const schoolIdFromUrl = searchParams.get('school_id');
  const effectiveSchoolId = schoolIdFromUrl || (isSuperAdmin ? selectedSchoolId : userSchoolId);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    student_id_number: '',
    grade: '',
    teacher_name: '',
    teacher_contact_id: '',
    school_name: isSuperAdmin ? '' : (userSchoolName || ''),
    school_id: effectiveSchoolId || '',
    concern_area: [],
    current_tier: 'Tier 2',
    support_start_date: new Date().toISOString().split('T')[0],
    review_date: '',
    status: 'New Student',
    notes: '',
    active: true
  });

  useEffect(() => {
    if (isSuperAdmin) {
      loadSchools();
    }
  }, [isSuperAdmin]);

  // Update form school_id when effectiveSchoolId is available (e.g., from user context)
  useEffect(() => {
    if (effectiveSchoolId && !formData.school_id) {
      setFormData(prev => ({ ...prev, school_id: effectiveSchoolId }));
    }
  }, [effectiveSchoolId]);

  useEffect(() => {
    // Update form school_id when effectiveSchoolId changes
    if (effectiveSchoolId) {
      setFormData(prev => ({ ...prev, school_id: effectiveSchoolId }));
    }
  }, [effectiveSchoolId]);
  
  // For non-Super Admins, auto-populate school_name from their assigned school
  useEffect(() => {
    if (!isSuperAdmin && userSchoolName && !formData.school_name) {
      setFormData(prev => ({ ...prev, school_name: userSchoolName }));
    }
  }, [isSuperAdmin, userSchoolName, formData.school_name]);

  useEffect(() => {
    // Find and set selected school info for display (Super Admin only)
    if (schools.length > 0 && effectiveSchoolId) {
      const school = schools.find(s => s.id === effectiveSchoolId);
      setSelectedSchool(school);
      if (school) {
        setFormData(prev => ({ ...prev, school_name: school.name }));
      }
    }
  }, [schools, effectiveSchoolId]);

  // Load staff contacts when school is selected
  useEffect(() => {
    if (effectiveSchoolId) {
      loadStaffContacts(effectiveSchoolId);
    }
  }, [effectiveSchoolId]);

  const loadStaffContacts = async (schoolId) => {
    setLoadingStaff(true);
    try {
      // Load all active staff contacts with email - any staff type can be assigned as a student's teacher
      const res = await apiService.getStaffContacts({ school_id: schoolId, active_only: true });
      setStaffContacts(res.data);
    } catch (error) {
      console.error('Failed to load staff contacts:', error);
    } finally {
      setLoadingStaff(false);
    }
  };

  const loadSchools = async () => {
    try {
      const response = await apiService.getSchools();
      setSchools(response.data);
    } catch (error) {
      console.error('Failed to load schools:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSchoolChange = (schoolId) => {
    const school = schools.find(s => s.id === schoolId);
    setSelectedSchool(school);
    setFormData({ 
      ...formData, 
      school_id: schoolId,
      school_name: school ? school.name : '',
      teacher_contact_id: '',  // Reset teacher when school changes
      teacher_name: ''
    });
    // Load staff contacts for the new school
    if (schoolId) {
      loadStaffContacts(schoolId);
    } else {
      setStaffContacts([]);
    }
  };

  const handleConcernToggle = (concern) => {
    const current = formData.concern_area;
    if (current.includes(concern)) {
      handleChange('concern_area', current.filter(c => c !== concern));
    } else {
      handleChange('concern_area', [...current, concern]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name || !formData.grade) {
      toast.error('Please fill in required fields');
      return;
    }

    if (formData.concern_area.length === 0) {
      toast.error('Please select at least one support area');
      return;
    }

    // For Super Admin, require school selection
    if (isSuperAdmin && !formData.school_id) {
      toast.error('Please select a school for this student');
      return;
    }

    // REQUIRED: Teacher assignment validation
    // Either select existing contact with email OR add by email
    if (teacherEntryMode === 'select') {
      if (!formData.teacher_contact_id) {
        toast.error('Please select a teacher or use "Add by Email" to assign one');
        return;
      }
      // Verify selected contact has email
      const selectedContact = staffContacts.find(c => c.id === formData.teacher_contact_id);
      if (selectedContact && !selectedContact.email) {
        toast.error('Selected teacher is missing an email address. Please select a teacher with email or use "Add by Email".');
        return;
      }
    } else if (teacherEntryMode === 'email') {
      if (!teacherEmail || !teacherEmail.includes('@')) {
        toast.error('Please enter a valid teacher email address');
        return;
      }
    }

    setLoading(true);
    try {
      let finalFormData = { ...formData };
      
      // If using email entry mode, find or create staff contact
      if (teacherEntryMode === 'email' && teacherEmail && teacherEmail.includes('@')) {
        setCreatingTeacher(true);
        try {
          const schoolId = formData.school_id || userSchoolId;
          const res = await apiService.findOrCreateStaffContact({
            school_id: schoolId,
            email: teacherEmail.toLowerCase().trim(),
            display_name: teacherDisplayName || null
          });
          finalFormData.teacher_contact_id = res.data.id;
          finalFormData.teacher_name = res.data.display_name;
        } catch (error) {
          console.error('Failed to create teacher contact:', error);
          toast.error(error.response?.data?.detail || 'Failed to link teacher');
          setLoading(false);
          setCreatingTeacher(false);
          return;
        }
        setCreatingTeacher(false);
      }
      
      const response = await apiService.createStudent(finalFormData);
      toast.success('Student created successfully');
      navigate(`/students/${response.data.id}`);
    } catch (error) {
      console.error('Failed to create student:', error);
      toast.error('Failed to create student');
    } finally {
      setLoading(false);
      setCreatingTeacher(false);
    }
  };

  return (
    <div className="page-container" data-testid="add-student-page">
      <div className="mb-6">
        <Link to="/students" className="inline-flex items-center text-[#595959] hover:text-[#1C1C1C] text-sm font-medium">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Students
        </Link>
      </div>

      <div className="page-header">
        <h1 className="page-title">Add New Student</h1>
        <p className="page-subtitle">Enter student information to begin tracking progress</p>
      </div>

      {/* Error state if non-Super Admin has no assigned school */}
      {!isSuperAdmin && !hasSchoolAssignment && (
        <div className="card" style={{ maxWidth: '800px' }}>
          <div className="card-content">
            <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Your account is not assigned to a school.</p>
                <p className="text-sm text-red-700 mt-1">Please contact a Super Admin to assign your account to a school before adding students.</p>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <Link to="/students">
              <Button type="button" variant="outline">Back to Students</Button>
            </Link>
          </div>
        </div>
      )}

      {/* Main form - only show if user has school assignment */}
      {(isSuperAdmin || hasSchoolAssignment) && (
      <div className="card" style={{ maxWidth: '800px' }}>
        <form onSubmit={handleSubmit}>
          <div className="card-content">
            <div className="form-grid">
              {/* Basic Info */}
              <div className="form-group">
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => handleChange('first_name', e.target.value)}
                  placeholder="Enter first name"
                  required
                  data-testid="first-name-input"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => handleChange('last_name', e.target.value)}
                  placeholder="Enter last name"
                  required
                  data-testid="last-name-input"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="student_id_number">Student ID #</Label>
                <Input
                  id="student_id_number"
                  value={formData.student_id_number}
                  onChange={(e) => handleChange('student_id_number', e.target.value)}
                  placeholder="Optional - school's student ID"
                  maxLength={50}
                  data-testid="student-id-input"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="grade">Grade *</Label>
                <Select value={formData.grade} onValueChange={(v) => handleChange('grade', v)}>
                  <SelectTrigger data-testid="grade-select">
                    <SelectValue placeholder="Select grade" />
                  </SelectTrigger>
                  <SelectContent>
                    {GRADES.map((grade) => (
                      <SelectItem key={grade} value={grade}>Grade {grade}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="form-group">
                <Label htmlFor="teacher_contact">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Teacher Email *
                  </div>
                </Label>
                <p className="text-xs text-[#595959] mb-2">
                  Teacher email is required so student assignments, progress updates, and future messaging stay connected correctly.
                </p>
                {isSuperAdmin && !formData.school_id ? (
                  <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                    Select a school first to assign a teacher
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Mode toggle */}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setTeacherEntryMode('select')}
                        className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                          teacherEntryMode === 'select'
                            ? 'bg-[#2C4C3B] text-white border-[#2C4C3B]'
                            : 'bg-white text-[#595959] border-[#E5E5E5] hover:border-[#2C4C3B]'
                        }`}
                      >
                        Select Existing
                      </button>
                      <button
                        type="button"
                        onClick={() => setTeacherEntryMode('email')}
                        className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                          teacherEntryMode === 'email'
                            ? 'bg-[#2C4C3B] text-white border-[#2C4C3B]'
                            : 'bg-white text-[#595959] border-[#E5E5E5] hover:border-[#2C4C3B]'
                        }`}
                      >
                        <UserPlus className="h-4 w-4 inline mr-1" />
                        Add by Email
                      </button>
                    </div>

                    {teacherEntryMode === 'select' ? (
                      <>
                        <Select 
                          value={formData.teacher_contact_id || ''} 
                          onValueChange={(v) => {
                            const contact = staffContacts.find(c => c.id === v);
                            setFormData(prev => ({
                              ...prev,
                              teacher_contact_id: v,
                              teacher_name: contact?.display_name || ''
                            }));
                          }}
                        >
                          <SelectTrigger data-testid="teacher-select">
                            <SelectValue placeholder={loadingStaff ? "Loading teachers..." : "Select a teacher with email"} />
                          </SelectTrigger>
                          <SelectContent>
                            {staffContacts.filter(c => c.email).map((contact) => (
                              <SelectItem key={contact.id} value={contact.id}>
                                {contact.display_name}
                                <span className="text-[#A3A3A3] ml-2">({contact.email})</span>
                              </SelectItem>
                            ))}
                            {staffContacts.filter(c => !c.email).length > 0 && (
                              <div className="px-2 py-1.5 text-xs text-amber-600 border-t border-[#E5E5E5] mt-1">
                                {staffContacts.filter(c => !c.email).length} teacher(s) hidden — missing email
                              </div>
                            )}
                          </SelectContent>
                        </Select>
                        {formData.school_id && staffContacts.filter(c => c.email).length === 0 && !loadingStaff && (
                          <p className="text-xs text-amber-600">
                            No teachers with email found. Use "Add by Email" to create a new teacher contact.
                          </p>
                        )}
                      </>
                    ) : (
                      <div className="space-y-3 p-3 bg-[#F3F4F1] rounded-lg border border-[#E5E5E5]">
                        <div>
                          <label className="block text-xs font-medium text-[#595959] mb-1">Teacher Email *</label>
                          <Input
                            type="email"
                            value={teacherEmail}
                            onChange={(e) => {
                              setTeacherEmail(e.target.value);
                              // Auto-suggest display name from email
                              if (e.target.value.includes('@')) {
                                const localPart = e.target.value.split('@')[0];
                                const parts = localPart.split(/[._-]/).map(p => 
                                  p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()
                                );
                                setTeacherDisplayName(parts.join(' '));
                              }
                            }}
                            placeholder="teacher@school.edu"
                            className="text-sm"
                            data-testid="teacher-email-input"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-[#595959] mb-1">Display Name</label>
                          <Input
                            type="text"
                            value={teacherDisplayName}
                            onChange={(e) => setTeacherDisplayName(e.target.value)}
                            placeholder="Suggested from email"
                            className="text-sm"
                            data-testid="teacher-display-name-input"
                          />
                          <p className="text-xs text-[#A3A3A3] mt-1">
                            Name will be suggested from email and can be edited.
                          </p>
                        </div>
                        {teacherEmail && teacherEmail.includes('@') && (
                          <p className="text-xs text-[#2C4C3B]">
                            {staffContacts.find(c => c.email?.toLowerCase() === teacherEmail.toLowerCase())
                              ? '✓ This teacher already exists and will be linked.'
                              : '+ A new teacher contact will be created when you save.'}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* School Selection for Super Admin */}
              {isSuperAdmin && (
                <div className="form-group full-width">
                  <Label htmlFor="school_id">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      School *
                    </div>
                  </Label>
                  {selectedSchool ? (
                    <div className="flex items-center gap-3">
                      <div className="flex-1 px-3 py-2 bg-indigo-50 border border-[#2C4C3B]/30 rounded-lg">
                        <span className="text-sm font-medium text-[#2C4C3B]">{selectedSchool.name}</span>
                        {selectedSchool.code && (
                          <span className="ml-2 text-xs text-indigo-500">({selectedSchool.code})</span>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedSchool(null);
                          setFormData(prev => ({ ...prev, school_id: '', school_name: '' }));
                        }}
                      >
                        Change
                      </Button>
                    </div>
                  ) : (
                    <Select 
                      value={formData.school_id} 
                      onValueChange={handleSchoolChange}
                    >
                      <SelectTrigger data-testid="school-select">
                        <SelectValue placeholder="Select a school" />
                      </SelectTrigger>
                      <SelectContent>
                        {schools.map((school) => (
                          <SelectItem key={school.id} value={school.id}>
                            {school.name} {school.code && `(${school.code})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              {/* School Name field - For non-Super Admin, show read-only with assigned school */}
              {!isSuperAdmin && (
                <div className="form-group full-width">
                  <Label htmlFor="school_name">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      School
                    </div>
                  </Label>
                  <div className="px-3 py-2 bg-[#F3F4F1] border border-[#E5E5E5] rounded-lg">
                    <span className="text-sm font-medium text-[#2C4C3B]">{userSchoolName || formData.school_name || 'Loading...'}</span>
                  </div>
                  <p className="text-xs text-[#595959] mt-1">Students are automatically assigned to your school</p>
                </div>
              )}

              {/* Initial Support Areas - toggle buttons */}
              <div className="form-group full-width">
                <Label>Support Area(s) *</Label>
                <p className="text-xs text-[#595959] mt-1 mb-3">Select all that apply</p>
                <div className="flex flex-wrap gap-2">
                  {SUPPORT_AREAS.map((area) => {
                    const isSelected = formData.concern_area.includes(area);
                    return (
                      <button
                        key={area}
                        type="button"
                        onClick={() => handleConcernToggle(area)}
                        className={`
                          inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium
                          transition-all duration-150 border-2
                          ${isSelected 
                            ? 'bg-[#2C4C3B] text-white border-indigo-600 shadow-sm' 
                            : 'bg-white text-[#595959] border-[#E5E5E5] hover:border-[#E5E5E5] hover:bg-[#F3F4F1]'
                          }
                        `}
                        data-testid={`support-area-${area.toLowerCase().replace(/[\s\/]+/g, '-')}`}
                      >
                        {isSelected && <Check className="h-4 w-4" />}
                        {area}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Tier and Status */}
              <div className="form-group">
                <Label htmlFor="current_tier">Current Tier</Label>
                <Select value={formData.current_tier} onValueChange={(v) => handleChange('current_tier', v)}>
                  <SelectTrigger data-testid="tier-select">
                    <SelectValue placeholder="Select tier" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIERS.map((tier) => (
                      <SelectItem key={tier} value={tier}>{tier}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="form-group">
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(v) => handleChange('status', v)}>
                  <SelectTrigger data-testid="status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Dates */}
              <div className="form-group">
                <Label htmlFor="support_start_date">Support Start Date</Label>
                <Input
                  id="support_start_date"
                  type="date"
                  value={formData.support_start_date}
                  onChange={(e) => handleChange('support_start_date', e.target.value)}
                  data-testid="start-date-input"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="review_date">Review Date</Label>
                <Input
                  id="review_date"
                  type="date"
                  value={formData.review_date}
                  onChange={(e) => handleChange('review_date', e.target.value)}
                  data-testid="review-date-input"
                />
              </div>

              {/* Notes */}
              <div className="form-group full-width">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="Enter any additional notes about this student"
                  rows={3}
                  data-testid="notes-input"
                />
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <Link to="/students">
              <Button type="button" variant="outline">Cancel</Button>
            </Link>
            <Button 
              type="submit" 
              className="bg-[#2C4C3B] hover:bg-[#1E3629]"
              disabled={loading}
              data-testid="save-student-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Student
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
      )}
    </div>
  );
}
